// EventTransactionsList — modal listing all transactions for a single
// event, opened by the cashier from the POS top bar to see what they've
// already sold without leaving POS. A sale can be voided from here too —
// the same dialog and the same rules as on the invoice page (permission,
// owner PIN, or an approved request), so a wrong sale is fixed at the till.

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Receipt, ExternalLink, ShoppingBag, Ban } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Chip } from '@/components/ui/Chip';
import { EmptyState } from '@/components/ui/EmptyState';
import { LoadingState } from '@/components/ui/LoadingState';
import { Table, THead, TBody, TR, TH, TD } from '@/components/ui/Table';
import { FitText } from '@/components/ui/FitText';
import { useTransactionsByEvent } from '@/hooks/useTransactions';
import { usePermissions } from '@/hooks/usePermissions';
import { useRequestsFor } from '@/hooks/useApprovals';
import { VoidTransactionDialog } from '@/components/transactions/VoidTransactionDialog';
import { formatIDR, formatDateTime, formatNumber } from '@/lib/format';
import type { AppTransaction, PaymentStatus } from '@/types/domain';

const STATUS_LABEL: Record<PaymentStatus, string> = {
  paid: 'Paid',
  pending_delivery: 'Pending',
  pending_payment: 'Tempo',
  overdue: 'Overdue',
  partially_returned: 'Partial return',
  returned: 'Returned',
  cancelled: 'Void',
};

const STATUS_VARIANT: Record<
  PaymentStatus,
  'success' | 'warning' | 'info' | 'danger' | 'default'
> = {
  paid: 'success',
  pending_delivery: 'warning',
  pending_payment: 'info',
  overdue: 'danger',
  partially_returned: 'info',
  returned: 'default',
  cancelled: 'danger',
};

export interface EventTransactionsListProps {
  open: boolean;
  onClose: () => void;
  eventId: string;
  eventName?: string;
}

export function EventTransactionsList({
  open,
  onClose,
  eventId,
  eventName,
}: EventTransactionsListProps) {
  // Only fetch when modal is open — saves reads on the POS hot path.
  const { data: transactions, isLoading } = useTransactionsByEvent(
    open ? eventId : null,
  );

  const sorted = (transactions ?? []).slice().sort((a, b) => b.createdAt - a.createdAt);

  const { can } = usePermissions();
  const [voidTarget, setVoidTarget] = useState<AppTransaction | null>(null);
  // The latest void request for the chosen sale, so the dialog can offer the
  // approved slip or say one is pending.
  const { data: voidRequests } = useRequestsFor('transaction.void', 'transaction_id', voidTarget?.id);

  const totals = sorted.reduce(
    (acc, t) => {
      if (t.paymentStatus === 'cancelled') return acc;
      acc.count += 1;
      acc.sales += t.grandTotal;
      return acc;
    },
    { count: 0, sales: 0 },
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Transaksi di event ini"
      description={
        eventName
          ? `Semua transaksi untuk "${eventName}". Klik invoice untuk lihat detail.`
          : 'Semua transaksi untuk event yang sedang aktif.'
      }
      // Wide enough for every column including the Void action — at xl the
      // last column fell off the right edge and needed a sideways scroll.
      size="2xl"
      footer={<Button variant="ghost" onClick={onClose}>Tutup</Button>}
    >
      {!isLoading && sorted.length > 0 && (
        <div className="mb-4 grid grid-cols-2 gap-3">
          <SummaryStat
            icon={<Receipt size={16} />}
            label="Jumlah transaksi"
            value={formatNumber(totals.count)}
          />
          <SummaryStat
            icon={<ShoppingBag size={16} />}
            label="Total penjualan"
            value={formatIDR(totals.sales)}
          />
        </div>
      )}

      {isLoading ? (
        <LoadingState message="Memuat transaksi…" />
      ) : sorted.length === 0 ? (
        <EmptyState
          icon={<Receipt size={24} strokeWidth={1.5} />}
          title="Belum ada transaksi"
          description="Belum ada penjualan di event ini. Transaksi pertama akan muncul di sini."
        />
      ) : (
        <div className="max-h-[55vh] overflow-y-auto overscroll-contain -mx-1 px-1">
          <Table>
            <THead>
              <TR>
                <TH>Invoice</TH>
                <TH>Waktu</TH>
                <TH>Customer</TH>
                <TH>Method</TH>
                <TH>Status</TH>
                <TH className="text-right">Total</TH>
                <TH className="text-right">Aksi</TH>
              </TR>
            </THead>
            <TBody>
              {sorted.map((t) => (
                <TR key={t.id}>
                  <TD>
                    <Link
                      to={`/invoice/${t.id}`}
                      target="_blank"
                      rel="noreferrer"
                      className="font-mono text-caption text-primary hover:underline whitespace-nowrap"
                    >
                      {t.invoiceNumber}
                    </Link>
                  </TD>
                  <TD>
                    <span className="text-caption text-ink-subtle whitespace-nowrap">
                      {formatDateTime(t.createdAt)}
                    </span>
                  </TD>
                  <TD>
                    <span className="text-small text-ink truncate max-w-[160px] inline-block">
                      {t.customerSnapshot?.name ?? '—'}
                    </span>
                  </TD>
                  <TD>
                    <span className="text-caption text-ink-subtle capitalize">
                      {t.paymentMethod}
                    </span>
                  </TD>
                  <TD>
                    <Chip variant={STATUS_VARIANT[t.paymentStatus]}>
                      {STATUS_LABEL[t.paymentStatus]}
                    </Chip>
                  </TD>
                  <TD className="text-right">
                    <span
                      className={
                        t.paymentStatus === 'cancelled'
                          ? 'text-ink-subtle line-through'
                          : 'font-medium text-ink whitespace-nowrap'
                      }
                    >
                      {formatIDR(t.grandTotal)}
                    </span>
                  </TD>
                  <TD>
                    <div className="flex items-center justify-end gap-1">
                      {/* Returned sales go through returns, not void — the
                          database refuses those anyway, so the button is not
                          offered. */}
                      {t.paymentStatus !== 'cancelled' && t.paymentStatus !== 'returned' && t.paymentStatus !== 'partially_returned' && (
                        <button
                          type="button"
                          onClick={() => setVoidTarget(t)}
                          className="inline-flex items-center gap-1 rounded px-2 py-1 text-caption font-medium text-danger hover:bg-danger/5"
                          title="Void transaksi ini"
                        >
                          <Ban size={13} />
                          Void
                        </button>
                      )}
                      <Link
                        to={`/invoice/${t.id}`}
                        target="_blank"
                        rel="noreferrer"
                        aria-label="Buka invoice di tab baru"
                        className="text-ink-subtle hover:text-primary inline-flex p-1"
                      >
                        <ExternalLink size={14} />
                      </Link>
                    </div>
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        </div>
      )}

      {voidTarget && (
        <VoidTransactionDialog
          open
          onClose={() => setVoidTarget(null)}
          transaction={voidTarget}
          canDirect={can('transactions', 'cancel')}
          request={voidRequests?.[0] ?? null}
        />
      )}
    </Modal>
  );
}

function SummaryStat({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="p-3 rounded-lg bg-canvas border border-line">
      <div className="flex items-center gap-1.5 text-overline uppercase text-ink-subtle font-medium">
        {icon}
        {label}
      </div>
      <div className="mt-1">
        <FitText className="text-metric font-display font-semibold text-ink tabular-nums">
          {value}
        </FitText>
      </div>
    </div>
  );
}
